import { InfoCircleIcon } from "@octopusdeploy/design-system-icons";
import type { Repository, AuthenticationConfigurationResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import { getAuthenticationConfiguration } from "~/areas/configuration/components/SigningKeys/getAuthenticationConfiguration";
import InternalLink from "~/components/Navigation/InternalLink";
import { Form, useForm } from "~/components/form/Form/Form";
import type { RotateExternalSigningKeyWorkflowInput } from "~/workflows/RotateExternalSigningKeyWorkflowInput";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import { workflows } from "~/workflows/workflows";
import * as styles from "./RotateExternalSigningKeyWorkflow.styles";
export async function rotateExternalSigningKeyWorkflowLoader(repository: Repository): Promise<RotateExternalSigningKeyWorkflowLoaderData> {
    const { authenticationConfiguration } = await getAuthenticationConfiguration(repository);
    return { authenticationConfiguration };
}
interface RotateExternalSigningKeyWorkflowLoaderData {
    authenticationConfiguration: AuthenticationConfigurationResource;
}
interface RotateExternalSigningKeyWorkflowProps {
    input: RotateExternalSigningKeyWorkflowInput;
    loaderData: RotateExternalSigningKeyWorkflowLoaderData | null;
    completeWorkflow: () => void;
}
type RotateSigningKeyModel = Record<string, never>;
type RotateSigningKeyOutput = {
    nextStep: "export" | "validate" | "complete";
};
type DialogStep = "initial" | "export" | "validate";
export function RotateExternalSigningKeyWorkflow({ input, loaderData, completeWorkflow }: RotateExternalSigningKeyWorkflowProps) {
    const [currentStep, setCurrentStep] = useState<DialogStep>("initial");
    const [pendingKeyId, setPendingKeyId] = useState<string | null>(null);
    const { formProps, createSaveAction } = useForm<RotateSigningKeyModel, RotateSigningKeyOutput>({
        formName: "Rotate Signing Keys",
        initialModel: {},
        onSubmit: async (repository) => {
            switch (currentStep) {
                case "initial": {
                    const pendingKey = await repository.SigningKeys.generatePendingKey();
                    setPendingKeyId(pendingKey.Id);
                    // Refetch the signing keys list in the background
                    input.refetchSigningKeys();
                    return { nextStep: "export" };
                }
                case "export":
                    // Trigger the download of signing keys
                    window.location.href = repository.SigningKeys.getExportLink(undefined, false);
                    return { nextStep: "validate" };
                case "validate":
                    // Activate the pending key when marking as validated
                    if (pendingKeyId) {
                        await repository.SigningKeys.activatePendingKey(pendingKeyId);
                    }
                    return { nextStep: "complete" };
            }
        },
        updateFormModelAfterSubmit: () => ({}),
        afterSubmit: async (_, output) => {
            switch (output.nextStep) {
                case "export":
                    setCurrentStep("export");
                    break;
                case "validate":
                    setCurrentStep("validate");
                    break;
                case "complete":
                    completeWorkflow();
                    break;
            }
        },
    });
    const oidcIssuerUrl = loaderData?.authenticationConfiguration.OidcConfigurationSettings.OidcIssuerUrl ?? "https://deploy.octopus.app";
    const getDialogTitle = () => {
        if (currentStep === "export")
            return "Export Signing Keys";
        if (currentStep === "validate")
            return "Validate Signing Keys";
        return "Rotate Signing Keys";
    };
    const getButtonLabel = () => {
        if (currentStep === "export")
            return "Export Signing Keys";
        if (currentStep === "validate")
            return "Mark as validated";
        return "Generate Signing Keys";
    };
    return (<WorkflowContent.Dialog workflow={workflows.rotateExternalSigningKeyWorkflow} title={getDialogTitle()} primaryAction={createSaveAction({ saveButtonLabel: getButtonLabel(), enabledWhenClean: true })}>
            <Form {...formProps}>
                {currentStep === "initial" && (<div className={styles.containerStyle}>
                        <div className={styles.iconStyle}>
                            <InfoCircleIcon size={24}/>
                        </div>
                        <div className={styles.contentStyle}>
                            <h4 className={styles.titleStyle}>What happens during key rotation</h4>
                            <p>When you rotate your signing keys, we'll generate a new set of keys while keeping your current keys active. This ensures your existing deployments continue working while you setup your public keys.</p>
                            <InternalLink to="#">Learn more</InternalLink>
                        </div>
                    </div>)}
                {currentStep === "export" && (<div className={styles.containerStyle}>
                        <div className={styles.iconStyle}>
                            <InfoCircleIcon size={24}/>
                        </div>
                        <div className={styles.contentStyle}>
                            <h4 className={styles.titleStyle}>About OIDC Issuer URL</h4>
                            <p>
                                We fetch your public keys from OIDC Issuer URL (currently set to <strong>{oidcIssuerUrl}</strong>) to verify requests come from your Octopus instance. repositories.
                            </p>
                            <p>Make sure this URL is publicly accessible via HTTPS.</p>
                        </div>
                    </div>)}
                {currentStep === "validate" && (<div>
                        <p>Once you've uploaded your exported signing keys, confirm the configuration is working.</p>
                    </div>)}
            </Form>
        </WorkflowContent.Dialog>);
}
